import React, { useState, useEffect } from 'react';
import { Sparkles, RefreshCw, Eye, X, ThumbsUp, ThumbsDown, RotateCcw, Rocket, Brain, Settings, BarChart3, ChevronDown, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const AutoTuning = () => {
  const [tabValue, setTabValue] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [modelInfo, setModelInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [suggestionDetailsOpen, setSuggestionDetailsOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [deploymentDetailsOpen, setDeploymentDetailsOpen] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [autoTuningConfig, setAutoTuningConfig] = useState({
    enabled: true, minConfidence: 0.75, autoApplyEnabled: false, autoApplyThreshold: 0.90, requireApproval: true
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [suggestionsRes, deploymentsRes, metricsRes, modelRes] = await Promise.all([
        api.get('/admin/routing/auto-tuning/suggestions'),
        api.get('/admin/routing/deployments'),
        api.get('/admin/routing/auto-tuning/metrics'),
        api.get('/admin/routing/auto-tuning/model')
      ]);
      setSuggestions(suggestionsRes.data.data || []);
      setDeployments(deploymentsRes.data.data || []);
      setMetrics(metricsRes.data.data || {});
      setModelInfo(modelRes.data.data || {});
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch auto-tuning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplySuggestion = async (id) => { try { await api.post(`/admin/routing/auto-tuning/suggestions/${id}/apply`, { reviewed_by: 'admin' }); fetchData(); } catch (e) { console.error(e); } };
  const handleRejectSuggestion = async (id) => { try { await api.post(`/admin/routing/auto-tuning/suggestions/${id}/reject`, { reviewed_by: 'admin' }); fetchData(); } catch (e) { console.error(e); } };
  const handleRollbackDeployment = async (id) => { try { await api.post(`/admin/routing/deployments/${id}/rollback`, { reason: 'Manual rollback from admin panel', trigger: 'MANUAL' }); fetchData(); } catch (e) { console.error(e); } };
  const handleConfigChange = (key, value) => setAutoTuningConfig(prev => ({ ...prev, [key]: value }));
  const handleSaveConfig = async () => { try { await api.put('/admin/routing/auto-tuning/config', autoTuningConfig); } catch (e) { console.error(e); } };

  const getStatusStyle = (status) => {
    if (status === 'APPLIED' || status === 'ACTIVE') return { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' };
    if (status === 'PENDING') return { background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.2)' };
    if (status === 'REJECTED' || status === 'ROLLED_BACK') return { background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' };
    return { background: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', border: '1px solid rgba(107, 114, 128, 0.2)' };
  };

  const getConfidenceBarColor = (c) => c >= 0.90 ? '#059669' : c >= 0.75 ? '#d97706' : '#dc2626';
  const formatPercentage = (v) => `${(v * 100).toFixed(1)}%`;
  const formatWeight = (v) => `${(v * 100).toFixed(0)}%`;
  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const tabs = [
    { label: 'ML Suggestions', icon: Brain },
    { label: 'Deployments', icon: Rocket },
    { label: 'Model', icon: BarChart3 },
    { label: 'Config', icon: Settings },
  ];

  const statCards = [
    { label: 'Pending', value: suggestions.filter(s => s.status === 'PENDING').length, color: '#d97706', bg: 'rgba(245, 158, 11, 0.06)', borderColor: 'rgba(245, 158, 11, 0.12)' },
    { label: 'Applied', value: suggestions.filter(s => s.status === 'APPLIED').length, color: '#059669', bg: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.12)' },
    { label: 'Model Accuracy', value: modelInfo.accuracy ? formatPercentage(modelInfo.accuracy) : 'N/A', color: '#4f46e5', bg: 'rgba(99, 102, 241, 0.06)', borderColor: 'rgba(99, 102, 241, 0.12)' },
    { label: 'Avg Improvement', value: metrics.avg_improvement_7d ? `${metrics.avg_improvement_7d.toFixed(1)}%` : 'N/A', color: '#059669', bg: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.12)' },
  ];

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            Auto Route Tuning
          </h1>
          <p className="text-sm text-gray-500 mt-1">ML-powered route optimization</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">{lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : ''}</span>
          <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm" style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)', color: '#4f46e5' }}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statCards.map((card, i) => (
          <div key={i} className="bg-white rounded-xl p-4" style={{ ...cardStyle, background: card.bg, borderColor: card.borderColor }}>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{card.label}</p>
            <span className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <div className="flex gap-0 px-2 pt-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          {tabs.map((tab, i) => {
            const Icon = tab.icon;
            return (
              <button key={i} onClick={() => setTabValue(i)} className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors ${tabValue === i ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`} style={tabValue === i ? { background: 'rgba(99, 102, 241, 0.06)', borderBottom: '2px solid #4f46e5' } : {}}>
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {/* ML Suggestions Tab */}
          {tabValue === 0 && (
            <div>
              <div className="mb-4">
                <button onClick={() => api.post('/admin/routing/auto-tuning/suggestions/generate').then(fetchData)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  <RefreshCw className="w-3.5 h-3.5" /> Generate Suggestions
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Route</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Confidence</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Improvement</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Current</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Suggested</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.length === 0 ? (
                      <tr><td colSpan="8" className="px-6 py-12 text-center"><Brain className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No suggestions</p></td></tr>
                    ) : suggestions.map((s) => (
                      <tr key={s.id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                        <td className="px-4 py-3 text-xs font-semibold text-gray-800">Route {s.route_id}</td>
                        <td className="px-4 py-3"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' }}>{s.type}</span></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-14 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${s.confidence_score * 100}%`, background: getConfidenceBarColor(s.confidence_score) }} /></div>
                            <span className="text-xs font-semibold text-gray-700">{formatPercentage(s.confidence_score)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-semibold text-green-600">+{s.expected_improvement.toFixed(1)}%</td>
                        <td className="px-4 py-3">
                          <div className="text-[9px] text-gray-500 space-y-0.5">
                            <div>SLA: {formatWeight(s.current_sla_weight)}</div>
                            <div>Health: {formatWeight(s.current_health_weight)}</div>
                            <div>Cost: {formatWeight(s.current_cost_weight)}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-[9px] font-semibold text-indigo-600 space-y-0.5">
                            <div>SLA: {formatWeight(s.suggested_sla_weight)}</div>
                            <div>Health: {formatWeight(s.suggested_health_weight)}</div>
                            <div>Cost: {formatWeight(s.suggested_cost_weight)}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusStyle(s.status)}>{s.status}</span></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-0.5">
                            <button onClick={() => { setSelectedSuggestion(s); setSuggestionDetailsOpen(true); }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="Details"><Eye className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button>
                            {s.status === 'PENDING' && <>
                              <button onClick={() => handleApplySuggestion(s.id)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-green-50" title="Apply"><ThumbsUp className="w-3.5 h-3.5 text-green-500" /></button>
                              <button onClick={() => handleRejectSuggestion(s.id)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50" title="Reject"><ThumbsDown className="w-3.5 h-3.5 text-red-500" /></button>
                            </>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Deployments Tab */}
          {tabValue === 1 && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Policy</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Phase</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Progress</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">SLA</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deployments.length === 0 ? (
                    <tr><td colSpan="8" className="px-6 py-12 text-center"><Rocket className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No deployments</p></td></tr>
                  ) : deployments.map((d) => (
                    <tr key={d.id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                      <td className="px-4 py-3 text-xs font-mono text-gray-600">{d.id}</td>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-800">{d.policy_name}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' }}>{d.phase}</span></td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusStyle(d.status)}>{d.status}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-14 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${d.progress}%`, background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }} /></div>
                          <span className="text-xs text-gray-600">{d.progress.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold" style={{ color: d.sla_improvement > 0 ? '#059669' : '#dc2626' }}>{d.sla_improvement > 0 ? '+' : ''}{d.sla_improvement.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-[10px] text-gray-500">{new Date(d.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-0.5">
                          <button onClick={() => { setSelectedDeployment(d); setDeploymentDetailsOpen(true); }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="Details"><Eye className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button>
                          {d.status === 'ACTIVE' && <button onClick={() => handleRollbackDeployment(d.id)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50" title="Rollback"><RotateCcw className="w-3.5 h-3.5 text-red-500" /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Model Performance Tab */}
          {tabValue === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <h4 className="text-sm font-bold text-gray-800 mb-4">Model Information</h4>
                <div className="space-y-2">
                  {[
                    ['Version', modelInfo.model_version || 'N/A'],
                    ['Type', modelInfo.model_type || 'N/A'],
                    ['Accuracy', modelInfo.accuracy ? formatPercentage(modelInfo.accuracy) : 'N/A'],
                    ['Last Trained', modelInfo.trained_at ? new Date(modelInfo.trained_at).toLocaleString() : 'N/A'],
                  ].map(([k, v], i) => (
                    <div key={i} className="flex justify-between py-2" style={i > 0 ? { borderTop: '1px solid rgba(99, 102, 241, 0.05)' } : {}}>
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{k}</span>
                      <span className="text-xs font-mono text-gray-800">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <h4 className="text-sm font-bold text-gray-800 mb-4">Performance Metrics</h4>
                <div className="rounded-lg p-3 mb-3" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">7-Day Average</span>
                  <div className="mt-1 space-y-1">
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Confidence</span><span className="font-semibold text-gray-800">{metrics.avg_confidence_7d ? formatPercentage(metrics.avg_confidence_7d) : 'N/A'}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Risk Score</span><span className="font-semibold text-gray-800">{metrics.avg_risk_7d ? formatPercentage(metrics.avg_risk_7d) : 'N/A'}</span></div>
                  </div>
                </div>
                <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Suggestions (24h)</h5>
                <div className="space-y-1">
                  {Object.entries(metrics.suggestion_counts_24h || {}).map(([status, count]) => (
                    <div key={status} className="flex justify-between py-1" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.05)' }}>
                      <span className="text-xs text-gray-500">{status}</span>
                      <span className="text-xs font-semibold text-gray-800">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Configuration Tab */}
          {tabValue === 3 && (
            <div className="bg-white rounded-xl p-5" style={cardStyle}>
              <h4 className="text-sm font-bold text-gray-800 mb-4">Auto-Tuning Configuration</h4>
              <div className="space-y-4">
                {[
                  { key: 'enabled', label: 'Enable Auto-Tuning', always: true },
                  { key: 'autoApplyEnabled', label: 'Auto-Apply High Confidence' },
                  { key: 'requireApproval', label: 'Require Manual Approval' },
                ].map(({ key, label, always }) => (
                  <label key={key} className="flex items-center justify-between py-2 cursor-pointer" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                    <span className="text-xs font-medium text-gray-700">{label}</span>
                    <div className="relative">
                      <input type="checkbox" className="sr-only peer" checked={autoTuningConfig[key]} onChange={(e) => handleConfigChange(key, e.target.checked)} disabled={!always && !autoTuningConfig.enabled} />
                      <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-indigo-500 transition-colors" />
                      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform" />
                    </div>
                  </label>
                ))}
                <div>
                  <div className="flex justify-between mb-1"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Min Confidence</span><span className="text-xs font-semibold" style={{ color: '#4f46e5' }}>{formatPercentage(autoTuningConfig.minConfidence)}</span></div>
                  <input type="range" className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{ accentColor: '#4f46e5' }} min={0.5} max={1.0} step={0.05} value={autoTuningConfig.minConfidence} onChange={(e) => handleConfigChange('minConfidence', parseFloat(e.target.value))} disabled={!autoTuningConfig.enabled} />
                </div>
                {autoTuningConfig.autoApplyEnabled && (
                  <div>
                    <div className="flex justify-between mb-1"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Auto-Apply Threshold</span><span className="text-xs font-semibold" style={{ color: '#4f46e5' }}>{formatPercentage(autoTuningConfig.autoApplyThreshold)}</span></div>
                    <input type="range" className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{ accentColor: '#4f46e5' }} min={0.8} max={1.0} step={0.05} value={autoTuningConfig.autoApplyThreshold} onChange={(e) => handleConfigChange('autoApplyThreshold', parseFloat(e.target.value))} disabled={!autoTuningConfig.enabled} />
                  </div>
                )}
                <div className="pt-2">
                  <button onClick={handleSaveConfig} className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>Save Configuration</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Suggestion Details Modal */}
      {suggestionDetailsOpen && selectedSuggestion && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl mb-8" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900">Suggestion Details</h3>
              <button onClick={() => setSuggestionDetailsOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-xs text-gray-600"><span className="font-semibold">Reason:</span> {selectedSuggestion.reason}</p>
              {['current_performance', 'ml_predictions', 'expected_outcomes'].map((section) => (
                selectedSuggestion.explanation?.[section] && (
                  <div key={section} className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                    <button onClick={() => toggleSection(section)} className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                      {section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expandedSections[section] ? 'rotate-180' : ''}`} />
                    </button>
                    {expandedSections[section] && (
                      <div className="px-4 pb-3 space-y-1" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
                        {Object.entries(selectedSuggestion.explanation[section]).map(([k, v]) => (
                          <div key={k} className="flex justify-between py-1">
                            <span className="text-[10px] text-gray-400">{k.replace(/_/g, ' ')}</span>
                            <span className="text-[10px] font-mono text-gray-700">{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              ))}
              {selectedSuggestion.status === 'PENDING' && (
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => { handleRejectSuggestion(selectedSuggestion.id); setSuggestionDetailsOpen(false); }} className="px-4 py-2 rounded-xl text-xs font-medium" style={{ color: '#dc2626', background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>Reject</button>
                  <button onClick={() => { handleApplySuggestion(selectedSuggestion.id); setSuggestionDetailsOpen(false); }} className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm hover:shadow-md" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>Apply</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Deployment Details Modal */}
      {deploymentDetailsOpen && selectedDeployment && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl mb-8" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900">Deployment Details</h3>
              <button onClick={() => setDeploymentDetailsOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between py-2"><span className="text-[10px] font-bold text-gray-400 uppercase">Status</span><span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getStatusStyle(selectedDeployment.status)}>{selectedDeployment.status}</span></div>
              <div className="flex justify-between py-2" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.05)' }}><span className="text-[10px] font-bold text-gray-400 uppercase">Phase</span><span className="text-xs font-mono text-gray-800">{selectedDeployment.phase}</span></div>
              {/* Timeline */}
              <div className="pt-2 space-y-0">
                {[
                  { time: selectedDeployment.created_at, label: 'Created', color: '#4f46e5', icon: Clock },
                  ...(selectedDeployment.started_at ? [{ time: selectedDeployment.started_at, label: 'Started', color: '#06b6d4', icon: Rocket }] : []),
                  ...(selectedDeployment.completed_at ? [{ time: selectedDeployment.completed_at, label: 'Completed', color: '#059669', icon: CheckCircle }] : []),
                  ...(selectedDeployment.rollback_at ? [{ time: selectedDeployment.rollback_at, label: 'Rolled Back', color: '#dc2626', icon: XCircle, sub: selectedDeployment.rollback_reason }] : []),
                ].map((step, i, arr) => {
                  const Icon = step.icon;
                  return (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${step.color}15`, border: `2px solid ${step.color}` }}><Icon className="w-3 h-3" style={{ color: step.color }} /></div>
                        {i < arr.length - 1 && <div className="w-0.5 h-6 bg-gray-200" />}
                      </div>
                      <div className="pb-3">
                        <p className="text-xs font-semibold text-gray-800">{step.label}</p>
                        <p className="text-[10px] text-gray-400">{new Date(step.time).toLocaleString()}</p>
                        {step.sub && <p className="text-[10px] text-red-500 mt-0.5">{step.sub}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selectedDeployment.status === 'ACTIVE' && (
                <div className="flex justify-end pt-2">
                  <button onClick={() => { handleRollbackDeployment(selectedDeployment.id); setDeploymentDetailsOpen(false); }} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium" style={{ color: '#dc2626', background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                    <RotateCcw className="w-3.5 h-3.5" /> Rollback
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoTuning;
